/**
 * LfoCard — animated LFO visualization + start/stop control. Supports sine
 * and sawtooth waveforms; the waveform is fixed per-instance.
 *
 * Visual:
 *   - SVG path drawing one full period of the chosen waveform across the width.
 *   - 16 dots overlaid on the curve (purely visual, the engine samples at
 *     ~30Hz regardless).
 *   - A playhead that rides the curve at the current phase.
 *   - State-driven styling:
 *       idle      — dim outline
 *       armed     — amber pulse, waiting for next downbeat
 *       running   — magenta glow, full opacity
 *       stopping  — fading, waiting for end-of-period
 *
 * Phase source:
 *   The playhead reads the BRIDGE-WIDE phase from smoothClock (60fps RAF),
 *   not the bridge's lfo.state.currentPhase (30Hz event-driven). That keeps
 *   the playhead smooth while lfo.state events update the engine's reported
 *   value + status.
 *
 *   Subtle: the bridge's bar (quantum=4 beats) and the LFO's period align
 *   visually only when periodBeats === quantum. For other period lengths
 *   the playhead would need startBeat to recompute its phase from continuous
 *   beat. Out of scope for now — UI assumes 1 bar period.
 */

import { createSignal, createMemo, createEffect, Show, type Accessor } from 'solid-js';
import type { LfoStateMessage, LfoWaveform } from '@opbuttons/schema';
import { lfoState } from '../lfo/runtime.ts';
import { phaseOffset, applyPhaseOffset } from '../clock/phaseOffset.ts';
import { armLfo, stopLfoAction, fanoutTargets } from '../opbridge/dispatch.ts';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import type { LfoItem } from '../layout/store.ts';
import { reportSend, controlName } from '../ui/pressReport.ts';

type Status = LfoStateMessage['state'];

export interface SineLfoProps {
  item: LfoItem;
  /** Continuous interpolated phase from smoothClock — 0..1 within bar. */
  phase: Accessor<number>;
  /** Beats per bar of the active clock source — drives the armed-countdown
   *  "in N beats to the next downbeat" (SD-7). */
  beatsPerBar: Accessor<number>;
}

// Armed-countdown ring geometry (SD-7). r=16 in a 40×40 viewBox.
const RING_R = 16;
const RING_C = 2 * Math.PI * RING_R;

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

// Convert user-facing min/max to bridge-facing center/amplitude.
export function deriveCenterAmplitude(min: number, max: number): { center: number; amplitude: number } {
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  return { center: (lo + hi) / 2, amplitude: (hi - lo) / 2 };
}

const POINTS = 16;
const W = 320;
const H = 160;
const MARGIN_X = 8;
const MID_Y = H / 2;
const AMP_PX = (H / 2) - 16;

// Wave-shape Y coordinates, mirroring the bridge's `sample()` function. SVG y
// grows downward, so we subtract the unit-range output from MID_Y.
function waveY(waveform: LfoWaveform, phase: number): number {
  switch (waveform) {
    case 'sine': return MID_Y - Math.sin(2 * Math.PI * phase) * AMP_PX;
    case 'saw':  return MID_Y - (2 * phase - 1) * AMP_PX;
  }
}

function buildPath(waveform: LfoWaveform): string {
  const samples = 80;
  let d = `M ${MARGIN_X} ${waveY(waveform, 0)}`;
  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    const x = MARGIN_X + t * (W - 2 * MARGIN_X);
    d += ` L ${x.toFixed(2)} ${waveY(waveform, t).toFixed(2)}`;
  }
  return d;
}

// Cache per-waveform paths so we don't rebuild on every render.
const PATH_BY_WAVEFORM: Record<LfoWaveform, string> = {
  sine: buildPath('sine'),
  saw: buildPath('saw'),
};

export function SineLfo(props: SineLfoProps) {
  const [status, setStatus] = createSignal<Status>('idle');
  const [reportedValue, setReportedValue] = createSignal(64);
  // Loop is local UI state; we send it to the bridge on each start. The
  // bridge echoes it back in lfo.state but we don't follow that — the
  // button is the source of truth for what the next start will request.
  const [loop, setLoop] = createSignal(true);

  // Reactive sync from the OPBridge-fed runtime store (opbridge/enginesStatePoll.ts
  // → lfo/runtime.ts) into local signals — local because onClick/onToggleLoop
  // below also write optimistically, ahead of the poll catching up. Gated on
  // `ts` advancing (not just any store update — the store is one signal for
  // ALL LFOs, so it fires on every poll tick regardless of which id changed)
  // so an optimistic flip isn't clobbered by an unrelated poll re-read of the
  // same still-stale snapshot for this id.
  let lastAppliedTs = 0;
  createEffect(() => {
    const snap = lfoState(props.item.id);
    if (snap.ts === lastAppliedTs) return;
    lastAppliedTs = snap.ts;
    setStatus(snap.state);
    setReportedValue(snap.currentValue);
  });

  const waveform = (): LfoWaveform => props.item.waveform;

  // Playhead x/y from the user-calibrated phase (raw + tap-on-1 offset).
  // Display-only correction; the bridge engine uses its own beat counter
  // and is unaffected.
  const displayPhase = createMemo(() => applyPhaseOffset(props.phase(), phaseOffset()));
  const playheadX = createMemo(() => MARGIN_X + displayPhase() * (W - 2 * MARGIN_X));
  const playheadY = createMemo(() => waveY(waveform(), displayPhase()));

  // Armed countdown (SD-7). While armed, the LFO is bar-quantized — it
  // engages at the next downbeat. Beats remaining in this bar = (1 - phase) ×
  // beatsPerBar; the ring drains as phase → 1 and the label counts down. Raw
  // phase (not offset-adjusted) since the engine arms on OPBridge's clock.
  const beatsRemaining = createMemo(() =>
    Math.max(0, (1 - clamp01(props.phase())) * Math.max(1, props.beatsPerBar())),
  );
  // Ring shows the REMAINING fraction of the bar: dashoffset = C × phase, so
  // at phase 0 the full ring is drawn, at phase 1 it's empty.
  const ringDashoffset = createMemo(() => RING_C * clamp01(props.phase()));
  const countdownLabel = createMemo(() =>
    beatsRemaining() < 0.08 ? 'now' : `in ${Math.ceil(beatsRemaining())}`,
  );

  const pathD = createMemo(() => PATH_BY_WAVEFORM[waveform()]);
  const dots = createMemo(() =>
    Array.from({ length: POINTS }, (_, i) => {
      const t = i / POINTS;
      return { x: MARGIN_X + t * (W - 2 * MARGIN_X), y: waveY(waveform(), t) };
    }),
  );

  function startArgs(extraLoop?: boolean) {
    const { center, amplitude } = deriveCenterAmplitude(props.item.min, props.item.max);
    return {
      lfoId: props.item.id,
      ccNumber: props.item.ccNumber,
      channel: props.item.channel,
      periodBeats: props.item.bars * 4,
      amplitude,
      center,
      waveform: waveform(),
      loop: extraLoop ?? loop(),
    };
  }

  // True when at least one of the LFO's configured bridges is connected —
  // drives the button-disabled state (multi-select bridge affinity,
  // 2026-08-13). (Legacy Node-bridge LFO arm removed 2026-07-18.)
  const anyTransportLive = () => anyBridgeConnectedReactive(props.item.bridgeIds);

  /** Arm the LFO on every configured bridge (multi-select fan-out,
   *  2026-08-13 — undefined bridgeIds resolves to the default bridge).
   *  Failures are logged but don't block the optimistic UI flip. (Legacy
   *  Node-bridge arm removed 2026-07-18.) */
  function dispatchArm(args: ReturnType<typeof startArgs>): Promise<boolean> {
    if (!anyTransportLive()) return Promise.resolve(false);
    {
      return Promise.all(fanoutTargets(props.item.bridgeIds).map((target) => armLfo({
        lfoId: args.lfoId,
        ccNumber: args.ccNumber,
        channel: args.channel,
        periodBeats: args.periodBeats,
        center: args.center,
        amplitude: args.amplitude,
        waveform: args.waveform,
        loop: args.loop,
      }, target))).then((allResults) => {
        reportSend(props.item.id, controlName(props.item, 'LFO'), allResults.flat());
        let anyOk = false;
        for (const results of allResults) for (const r of results) {
          if (r.kind === 'ok') anyOk = true;
          else console.warn(`[SineLfo] armLfo → ${r.target}: ${r.kind}${r.detail ? ' - ' + r.detail : ''}`);
        }
        return anyOk;
      });
    }
  }

  /** Arm, showing 'armed' at once; if no computer took it, back to idle (2026-11-03 audit A-5: the optimistic flip
   *  stayed 'armed', counting down, forever, because no snapshot would ever list an LFO that was never armed). */
  function armShown(): void {
    setStatus('armed');
    void dispatchArm(startArgs()).then((ok) => { if (!ok && status() === 'armed') setStatus('idle'); });
  }

  function dispatchStop(): void {
    if (anyTransportLive()) {
      void Promise.all(fanoutTargets(props.item.bridgeIds).map((target) =>
        stopLfoAction(props.item.id, target),
      )).then((allResults) => {
        for (const results of allResults) for (const r of results) {
          if (r.kind !== 'ok') {
            console.warn(`[SineLfo] stopLfo → ${r.target}: ${r.kind}${r.detail ? ' - ' + r.detail : ''}`);
          }
        }
      });
    }
  }

  function onClick() {
    if (!anyTransportLive()) return;
    const s = status();
    if (s === 'idle') {
      // Optimistic, then corrected by the poll; reverted if nothing took it.
      armShown();
    } else if (s === 'armed' || s === 'running') {
      dispatchStop();
      setStatus('stopping');
    } else if (s === 'stopping') {
      armShown();
    }
  }

  function onToggleLoop() {
    const next = !loop();
    setLoop(next);
    // If we're already running, push the change live so the engine respects
    // the new loop setting at the next period wrap. Idle/armed don't need it.
    const s = status();
    if (s === 'running' || s === 'stopping') {
      dispatchArm(startArgs(next));
    }
  }

  const buttonLabel = createMemo(() => {
    switch (status()) {
      case 'idle':     return 'START';
      case 'armed':    return 'CANCEL';
      case 'running':  return 'STOP';
      case 'stopping': return 'RESUME';
    }
  });

  const itemName = () => props.item.name ?? '';
  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel;

  return (
    <div class="sine-lfo" data-state={status()} data-waveform={waveform()}>
      {showName() && itemName() && (
        <div class="sine-lfo-name">{itemName()}</div>
      )}
      <svg
        class="sine-lfo-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        {/* baseline */}
        <line
          class="sine-lfo-axis"
          x1={MARGIN_X} y1={MID_Y}
          x2={W - MARGIN_X} y2={MID_Y}
        />
        {/* curve */}
        <path class="sine-lfo-curve" d={pathD()} />
        {/* 16 dots */}
        {dots().map(({ x, y }) => (
          <circle class="sine-lfo-dot" cx={x} cy={y} r="2.5" />
        ))}
        {/* playhead */}
        <Show when={status() !== 'idle'}>
          <circle
            class="sine-lfo-playhead"
            cx={playheadX()}
            cy={playheadY()}
            r="6"
          />
        </Show>
      </svg>

      {/* Armed countdown (SD-7) — a draining ring + "in N beats" to the next
          downbeat, shown only while armed-pending. Overlays the curve. */}
      <Show when={status() === 'armed'}>
        <div class="sine-lfo-countdown" aria-hidden="true">
          <svg class="sine-lfo-countdown-ring" viewBox="0 0 40 40">
            <circle class="cd-track" cx="20" cy="20" r={RING_R} />
            <circle
              class="cd-drain"
              cx="20" cy="20" r={RING_R}
              stroke-dasharray={String(RING_C)}
              stroke-dashoffset={String(ringDashoffset())}
            />
          </svg>
          <span class="sine-lfo-countdown-label">{countdownLabel()}</span>
        </div>
      </Show>

      <div class="sine-lfo-controls">
        <button
          class="sine-lfo-button"
          type="button"
          onClick={onClick}
          disabled={!anyTransportLive()}
          data-state={status()}
        >
          {buttonLabel()}
        </button>
        <button
          class="sine-lfo-loop"
          type="button"
          onClick={onToggleLoop}
          data-on={loop() ? 'true' : 'false'}
          title={loop() ? 'Looping - tap to make one-shot' : 'One-shot - tap to loop'}
          aria-pressed={loop() ? 'true' : 'false'}
        >
          {loop() ? 'LOOP' : '1-SHOT'}
        </button>
      </div>

      {showLabel() && (
        <div class="sine-lfo-meta">
          <span><b>{waveform()}</b></span>
          <span>cc <b>{props.item.ccNumber}</b></span>
          <span>ch <b>{props.item.channel + 1}</b></span>
          <span>val <b>{reportedValue()}</b></span>
          <span class="sine-lfo-status">{status()}</span>
        </div>
      )}
    </div>
  );
}
